import type { SortingState } from '@tanstack/react-table'
import type { CellContext } from '@tanstack/react-table'
import type { ReactNode } from 'react'
import type {
  FieldDefinition,
  FieldDefinitionMetadata,
  ViewColumn,
  Sort,
} from '@/types/api'

/**
 * Data types from compass-be FieldDefinition.dataType
 * Maps to: string, number, boolean, date, datetime, enum, multiselect, uuid, object, array
 */
export type DataType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'date'
  | 'datetime'
  | 'enum'
  | 'multiselect'
  | 'uuid'
  | 'object'
  | 'array'

/**
 * Get nested value from object using dot notation path
 * e.g., getNestedValue({ financials: { ltmRevenue: 100 } }, 'financials.ltmRevenue') => 100
 */
export function getNestedValue(obj: unknown, path: string): unknown {
  if (!obj || typeof obj !== 'object') return undefined
  const keys = path.split('.')
  let value: unknown = obj
  for (const key of keys) {
    if (value === null || value === undefined) return undefined
    value = (value as Record<string, unknown>)[key]
  }
  return value
}

/**
 * Column definition with field metadata for DataTable
 */
export interface ColumnDef<TData> {
  /** Column ID (usually same as fieldName) */
  id: string
  /** Accessor key for getting cell value from row data */
  accessorKey?: keyof TData | string
  /** Accessor function for nested/computed values */
  accessorFn?: (row: TData) => unknown
  /**
   * Header content, or a function rendering it.
   *
   * The table renders this through `flexRender`, so a function has always
   * worked; the type said string and disagreed with both the comment above it
   * and the runtime. Widened so a column can put a control in its header, which
   * the Deal Review inbox needs for select-all.
   */
  header: string | (() => ReactNode)
  /** Field name from FieldDefinition */
  fieldName: string
  /** Display name for the column header */
  displayName: string
  /** Data type for cell rendering */
  dataType: DataType
  /** Whether the column can be filtered */
  isFilterable: boolean
  /** Whether the column can be sorted */
  isSortable: boolean
  /** Whether the column values can be edited */
  isEditable: boolean
  /** Available options for enum/multiselect types */
  options?: string[]
  /** Column width in pixels */
  width?: number
  /** Display order in the table */
  displayOrder: number
  /** Whether the column is visible */
  isVisible: boolean
  /** Column meta for TanStack Table */
  meta: ColumnMeta
  /** Optional custom cell renderer */
  cell?: (info: CellContext<TData, unknown>) => ReactNode
}

/**
 * Column meta information attached to TanStack Table columns
 */
export type BadgeVariant = 'outline' | 'outline-plain' | 'solid'

export type FieldSourceCode = FieldDefinitionMetadata['dataSource']

export interface ColumnMeta {
  fieldName: string
  displayName: string
  dataType: DataType
  isFilterable: boolean
  isSortable: boolean
  isEditable: boolean
  /** Data source for header indicator (info vs pencil) */
  dataSource?: FieldSourceCode
  options?: string[]
  /** Badge style variant for enum/multiselect types */
  badgeVariant?: BadgeVariant
  /** Property to extract from objects for display (e.g., 'abbreviation', 'name') */
  displayProperty?: string
  /** Display format for numbers (e.g., 'currency', 'percentage') */
  format?: string
}

/**
 * Props for DataTable component
 */
export interface DataTableProps<TData extends Record<string, unknown>> {
  /** Table data rows */
  data: TData[]
  /** Column definitions with field metadata */
  columns: ColumnDef<TData>[]
  /** Whether data is loading */
  isLoading?: boolean
  /** Whether more data is being loaded */
  isLoadingMore?: boolean
  /** Whether there is more data to load */
  hasMore?: boolean
  /** Current sorting state */
  sorting?: SortingState
  /** Callback when sorting changes */
  onSortChange?: (sorting: SortingState) => void
  /** Callback to load more data */
  onLoadMore?: () => void
  /** Callback when a row is clicked */
  onRowClick?: (row: TData) => void
  /** Search term for highlighting */
  searchTerm?: string
  /** Optional class name */
  className?: string
}

/**
 * Props for cell components
 */
export interface CellProps<TValue = unknown> {
  /** Cell value */
  value: TValue
  /** Column definition (optional for simple cell rendering) */
  column?: ColumnMeta
  /** Whether the cell is being edited */
  isEditing?: boolean
  /** Search term for highlighting */
  searchTerm?: string
  /** Optional class name */
  className?: string
}

/**
 * Badge configuration for relationship fields
 */
export function getBadgeConfig(fieldName: string): {
  badgeVariant?: BadgeVariant
  displayProperty?: string
} {
  switch (fieldName) {
    case 'funds':
      return { badgeVariant: 'solid', displayProperty: 'abbreviation' }
    case 'sectors':
    case 'industries':
      return { badgeVariant: 'outline-plain', displayProperty: 'name' }
    case 'location':
      return { badgeVariant: 'solid', displayProperty: 'name' }
    default:
      return {}
  }
}

/**
 * Build column definitions from view columns and field definitions
 */
export function buildColumnDefs<TData extends Record<string, unknown>>(
  viewColumns: ViewColumn[],
  fieldDefinitions: FieldDefinition[]
): ColumnDef<TData>[] {
  // Don't filter by isVisible here - let the consumer handle visibility
  return viewColumns
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((viewCol) => {
      // Get field metadata from enriched response or lookup from field definitions
      const field: FieldDefinitionMetadata | undefined =
        viewCol.field ??
        fieldDefinitions.find((f) => f.fieldName === viewCol.fieldName)

      const dataType = (field?.dataType ?? 'string') as DataType
      const isNestedPath = viewCol.fieldName.includes('.')
      // Use double underscores in ID to prevent TanStack Table from parsing as path
      // Double underscore is unlikely to appear in field names
      const columnId = viewCol.fieldName.replace(/\./g, '__')

      // Build column def with either accessorKey (simple) or accessorFn (nested)
      const columnDef: ColumnDef<TData> = {
        id: columnId,
        header: field?.displayName ?? viewCol.fieldName,
        fieldName: viewCol.fieldName,
        displayName: field?.displayName ?? viewCol.fieldName,
        dataType,
        isFilterable: field?.isFilterable ?? false,
        isSortable: field?.isSortable ?? false,
        isEditable: field?.isEditable ?? false,
        options: field?.options,
        width: viewCol.width ?? undefined,
        displayOrder: viewCol.displayOrder,
        isVisible: viewCol.isVisible,
        meta: {
          fieldName: viewCol.fieldName,
          displayName: field?.displayName ?? viewCol.fieldName,
          dataType,
          isFilterable: field?.isFilterable ?? false,
          isSortable: field?.isSortable ?? false,
          isEditable: field?.isEditable ?? false,
          dataSource: field?.dataSource,
          options: field?.options,
          format: field?.format,
          // Configure badge styling for relationship fields
          ...getBadgeConfig(viewCol.fieldName),
        },
      }

      // Always use accessorFn to avoid TanStack Table's internal path-parsing
      // which logs warnings for undefined nested values
      columnDef.accessorFn = (row: TData) =>
        isNestedPath
          ? getNestedValue(row, viewCol.fieldName)
          : row[viewCol.fieldName as keyof TData]

      return columnDef
    })
}

// Convert column ID (double underscores) to API field name (dots)
export function columnIdToFieldName(id: string): string {
  return id.replace(/__/g, '.')
}

// Convert API field name (dots) to column ID (double underscores)
export function fieldNameToColumnId(fieldName: string): string {
  return fieldName.replace(/\./g, '__')
}

/**
 * Convert TanStack SortingState to API SortDto array
 */
export function sortingStateToSortDto(sorting: SortingState): Sort[] {
  return sorting.map((sort, index) => ({
    field: columnIdToFieldName(sort.id),
    direction: sort.desc ? 'desc' : 'asc',
    order: index,
  }))
}

/**
 * ViewSort from API has different field names than SortDto
 */
interface ViewSortLike {
  fieldName: string
  direction: string
  sortOrder: number
}

/**
 * Convert API ViewSort array to TanStack SortingState
 */
export function viewSortsToSortingState(sorts: ViewSortLike[]): SortingState {
  return [...sorts]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((sort) => ({
      id: fieldNameToColumnId(sort.fieldName),
      desc: sort.direction.toLowerCase() === 'desc',
    }))
}

/**
 * Convert API SortDto array to TanStack SortingState
 */
export function sortDtoToSortingState(sorts: Sort[]): SortingState {
  return [...sorts]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((sort) => ({
      id: fieldNameToColumnId(sort.field),
      desc: sort.direction === 'desc',
    }))
}
